/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';

export default class UsersController extends Controller {
  @service
  session;

  @service
  store;

  @tracked
  userId = '';

  @tracked
  username = '';

  @action
  setUsername(value) {
    this.username = value;
  }

  @tracked
  password = '';

  @action
  setPassword(value) {
    this.password = value;
  }

  @tracked
  schoolId = null;

  @action
  setSchoolId(value) {
    this.schoolId = value;
  }

  @tracked
  userDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteUserDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  // toggleDialog opens the user dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('userDialogOpen');
    if (!this.userDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleUserDialog() {
    this.toggleDialog();
  }

  @action
  editUser(id) {
    this.store.findRecord('user', id).then((person) => {
      this.username = person.get('username');
      this.schoolId = person.get('schoolId');
    });
    this.userId = id;
    this.toggleDialog();
  }

  @action
  saveUser() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.userId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('user', {
          username: this.username,
          password: this.password,
          schoolId: this.schoolId,
          permission: 'USER',
        });

        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.userId;
        this.store.findRecord('user', id).then((person) => {
          person.set('username', this.username);
          person.set('password', this.password);
          person.set('schoolId', this.schoolId);

          resolve(person);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          this.userDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteUser(id) {
    this.errorMessage = '';
    this.store.findRecord('user', id).then((person) => {
      this.username = person.get('username');
    });
    this.userId = id;
    this.deleteUserDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let person = this.store.peekRecord('user', this.userId);

        // Delete record (locally + pushed to server).
        return person
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            person.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteUserDialogOpen = false;
        this.userId = '';
        this.username = '';
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  init() {
    super.init(...arguments);
    this.schoolId = this.get('session.user.schoolId');
  }

  handleError(err, action) {
    if (!isEmpty(err.errors[0].status)) {
      this.setErrorMessage(err.errors[0].status, action);
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  resetForm() {
    this.userId = '';
    this.username = '';
    this.password = '';
    this.schoolId = this.get('session.user.schoolId');
  }
}
